/**
 * useNotifications Hook
 *
 * React hook for real-time notifications using Server-Sent Events (SSE).
 * Provides notifications array, unread count, and methods for managing notifications.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  notificationSSE,
  notificationsApi,
  type Notification,
  type SSEConnectionState,
  type NotificationType,
  getNotificationPriority,
} from '@/services/notifications';

// ============================================================================
// Types
// ============================================================================

export interface UseNotificationsOptions {
  /** Auto-connect to SSE on mount (default: true) */
  autoConnect?: boolean;
  /** Enable localStorage persistence (default: true) */
  persistToStorage?: boolean;
  /** Maximum notifications to keep in state (default: 50) */
  maxNotifications?: number;
  /** Show toast for new notifications (default: true) */
  showToasts?: boolean;
  /** Only show toasts for specific priority levels */
  toastPriorityThreshold?: 'critical' | 'high' | 'medium' | 'low';
}

export interface UseNotificationsReturn {
  /** Array of notifications */
  notifications: Notification[];
  /** Number of unread notifications */
  unreadCount: number;
  /** SSE connection state */
  connectionState: SSEConnectionState;
  /** Whether notifications are loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Mark a single notification as read */
  markAsRead: (id: string) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Clear all notifications from state */
  clearAll: () => void;
  /** Delete a notification */
  deleteNotification: (id: string) => Promise<void>;
  /** Refresh notifications from server */
  refresh: () => Promise<void>;
  /** Manually connect to SSE */
  connect: () => void;
  /** Manually disconnect from SSE */
  disconnect: () => void;
}

export interface ToastNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
  timestamp: number;
}

// ============================================================================
// Toast Notification Manager
// ============================================================================

type ToastCallback = (toast: ToastNotification) => void;

class ToastManager {
  private listeners: Set<ToastCallback> = new Set();

  subscribe(callback: ToastCallback): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  show(toast: Omit<ToastNotification, 'id' | 'timestamp'>): void {
    const fullToast: ToastNotification = {
      ...toast,
      id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };
    this.listeners.forEach((callback) => callback(fullToast));
  }

  info(title: string, message: string, duration?: number): void {
    this.show({ title, message, type: 'info', duration });
  }

  success(title: string, message: string, duration?: number): void {
    this.show({ title, message, type: 'success', duration });
  }

  warning(title: string, message: string, duration?: number): void {
    this.show({ title, message, type: 'warning', duration });
  }

  error(title: string, message: string, duration?: number): void {
    this.show({ title, message, type: 'error', duration });
  }
}

export const toastManager = new ToastManager();

// ============================================================================
// Storage Keys
// ============================================================================

const STORAGE_KEY = 'ohcs_notifications';
const UNREAD_COUNT_KEY = 'ohcs_notifications_unread';

// ============================================================================
// Helper Functions
// ============================================================================

function getToastTypeFromNotificationType(type: NotificationType): ToastNotification['type'] {
  const priority = getNotificationPriority(type);
  switch (priority) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    default:
      return 'info';
  }
}

function shouldShowToast(
  notificationType: NotificationType,
  threshold: 'critical' | 'high' | 'medium' | 'low'
): boolean {
  const priorityOrder = ['low', 'medium', 'high', 'critical'];
  const notificationPriority = getNotificationPriority(notificationType);
  const notificationIndex = priorityOrder.indexOf(notificationPriority);
  const thresholdIndex = priorityOrder.indexOf(threshold);
  return notificationIndex >= thresholdIndex;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useNotifications(options: UseNotificationsOptions = {}): UseNotificationsReturn {
  const {
    autoConnect = true,
    persistToStorage = true,
    maxNotifications = 50,
    showToasts = true,
    toastPriorityThreshold = 'medium',
  } = options;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isMounted = useRef(true);

  // Load from storage on mount
  useEffect(() => {
    if (persistToStorage) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as Notification[];
          setNotifications(parsed.slice(0, maxNotifications));
        }
        const storedCount = localStorage.getItem(UNREAD_COUNT_KEY);
        if (storedCount) {
          setUnreadCount(parseInt(storedCount, 10) || 0);
        }
      } catch (err) {
        console.error('[useNotifications] Failed to load from storage:', err);
      }
    }
  }, [persistToStorage, maxNotifications]);

  // Persist to storage when notifications change
  useEffect(() => {
    if (persistToStorage && notifications.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
        localStorage.setItem(UNREAD_COUNT_KEY, String(unreadCount));
      } catch (err) {
        console.error('[useNotifications] Failed to save to storage:', err);
      }
    }
  }, [notifications, unreadCount, persistToStorage]);

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    if (!isMounted.current) return;

    setIsLoading(true);
    setError(null);

    try {
      const [notificationsResponse, countResponse] = await Promise.all([
        notificationsApi.list({ perPage: maxNotifications }),
        notificationsApi.getUnreadCount(),
      ]);

      if (!isMounted.current) return;

      if (notificationsResponse.success && notificationsResponse.data) {
        setNotifications(notificationsResponse.data);
      }

      if (countResponse.success && countResponse.data) {
        setUnreadCount(countResponse.data.count);
      }
    } catch (err) {
      if (!isMounted.current) return;
      const message = err instanceof Error ? err.message : 'Failed to fetch notifications';
      setError(message);
      console.error('[useNotifications] Fetch error:', err);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [maxNotifications]);

  // Handle new notification from SSE
  const handleNewNotification = useCallback(
    (data: unknown) => {
      const notification = data as Notification;

      setNotifications((prev) => {
        // Check if notification already exists
        if (prev.some((n) => n.id === notification.id)) {
          return prev;
        }

        // Add to beginning and trim to max
        const updated = [notification, ...prev].slice(0, maxNotifications);
        return updated;
      });

      setUnreadCount((prev) => prev + 1);

      // Show toast notification
      if (showToasts && shouldShowToast(notification.type, toastPriorityThreshold)) {
        toastManager.show({
          title: notification.title,
          message: notification.message,
          type: getToastTypeFromNotificationType(notification.type),
          duration: 5000,
        });
      }
    },
    [maxNotifications, showToasts, toastPriorityThreshold]
  );

  // Handle notification read event from SSE
  const handleNotificationRead = useCallback((data: unknown) => {
    const { id } = data as { id: string };

    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
    );

    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Handle workflow pending event
  const handleWorkflowPending = useCallback(
    (data: unknown) => {
      const { documentTitle, actionRequired } = data as {
        taskId: string;
        documentTitle: string;
        actionRequired: string;
      };

      if (showToasts) {
        toastManager.info(
          'Workflow Task Pending',
          `${actionRequired} required for "${documentTitle}"`,
          7000
        );
      }

      // Refresh notifications to get the full notification object
      fetchNotifications();
    },
    [showToasts, fetchNotifications]
  );

  // Handle document updated event
  const handleDocumentUpdated = useCallback(
    (data: unknown) => {
      const { documentTitle, action } = data as {
        documentId: string;
        documentTitle: string;
        action: string;
      };

      if (showToasts) {
        toastManager.info('Document Updated', `"${documentTitle}" was ${action}`, 5000);
      }
    },
    [showToasts]
  );

  // Handle pfile movement event
  const handlePFileMovement = useCallback(
    (data: unknown) => {
      const { pfileNumber, fromDepartment, toDepartment } = data as {
        pfileId: string;
        pfileNumber: string;
        fromDepartment: string;
        toDepartment: string;
      };

      if (showToasts) {
        toastManager.info(
          'P-File Movement',
          `P-File ${pfileNumber} moved from ${fromDepartment} to ${toDepartment}`,
          6000
        );
      }
    },
    [showToasts]
  );

  // Handle system announcement event
  const handleSystemAnnouncement = useCallback(
    (data: unknown) => {
      const { title, message, priority } = data as {
        title: string;
        message: string;
        priority?: 'info' | 'warning' | 'critical';
      };

      const toastType = priority === 'critical' ? 'error' : priority === 'warning' ? 'warning' : 'info';
      const duration = priority === 'critical' ? 10000 : 7000;

      toastManager.show({
        title,
        message,
        type: toastType,
        duration,
      });
    },
    []
  );

  // Setup SSE connection and listeners
  useEffect(() => {
    isMounted.current = true;

    // Subscribe to connection state changes
    const unsubscribeState = notificationSSE.onStateChange(setConnectionState);

    // Set initial state
    setConnectionState(notificationSSE.getConnectionState());

    // Subscribe to events
    notificationSSE.subscribe('notification:new', handleNewNotification);
    notificationSSE.subscribe('notification:read', handleNotificationRead);
    notificationSSE.subscribe('workflow:pending', handleWorkflowPending);
    notificationSSE.subscribe('document:updated', handleDocumentUpdated);
    notificationSSE.subscribe('pfile:movement', handlePFileMovement);
    notificationSSE.subscribe('system:announcement', handleSystemAnnouncement);

    // Connect if auto-connect is enabled
    if (autoConnect) {
      notificationSSE.connect();
    }

    // Fetch initial notifications
    fetchNotifications();

    return () => {
      isMounted.current = false;
      unsubscribeState();
      notificationSSE.unsubscribe('notification:new', handleNewNotification);
      notificationSSE.unsubscribe('notification:read', handleNotificationRead);
      notificationSSE.unsubscribe('workflow:pending', handleWorkflowPending);
      notificationSSE.unsubscribe('document:updated', handleDocumentUpdated);
      notificationSSE.unsubscribe('pfile:movement', handlePFileMovement);
      notificationSSE.unsubscribe('system:announcement', handleSystemAnnouncement);
    };
  }, [
    autoConnect,
    fetchNotifications,
    handleNewNotification,
    handleNotificationRead,
    handleWorkflowPending,
    handleDocumentUpdated,
    handlePFileMovement,
    handleSystemAnnouncement,
  ]);

  // Mark single notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      const response = await notificationsApi.markAsRead(id);
      if (response.success) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('[useNotifications] Failed to mark as read:', err);
      throw err;
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await notificationsApi.markAllAsRead();
      if (response.success) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('[useNotifications] Failed to mark all as read:', err);
      throw err;
    }
  }, []);

  // Clear all notifications from state
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    if (persistToStorage) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(UNREAD_COUNT_KEY);
    }
  }, [persistToStorage]);

  // Delete a notification
  const deleteNotification = useCallback(async (id: string) => {
    try {
      const response = await notificationsApi.delete(id);
      if (response.success) {
        setNotifications((prev) => {
          const notification = prev.find((n) => n.id === id);
          if (notification && !notification.isRead) {
            setUnreadCount((count) => Math.max(0, count - 1));
          }
          return prev.filter((n) => n.id !== id);
        });
      }
    } catch (err) {
      console.error('[useNotifications] Failed to delete notification:', err);
      throw err;
    }
  }, []);

  // Refresh notifications from server
  const refresh = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Manual connect
  const connect = useCallback(() => {
    notificationSSE.connect();
  }, []);

  // Manual disconnect
  const disconnect = useCallback(() => {
    notificationSSE.disconnect();
  }, []);

  return {
    notifications,
    unreadCount,
    connectionState,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    clearAll,
    deleteNotification,
    refresh,
    connect,
    disconnect,
  };
}

// ============================================================================
// Toast Hook
// ============================================================================

/**
 * Hook for displaying and managing toast notifications
 */
export function useToasts(): {
  toasts: ToastNotification[];
  removeToast: (id: string) => void;
  clearToasts: () => void;
} {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((toast) => {
      setToasts((prev) => [...prev, toast]);

      // Auto-remove after duration
      if (toast.duration) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== toast.id));
        }, toast.duration);
      }
    });

    return unsubscribe;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return { toasts, removeToast, clearToasts };
}
